/**
 * EmployeeIdCard.js — Digital QR Employee ID Card
 *
 * FEATURE (this version) — QR code now links to a PUBLIC verification
 * page (/verify/:uuid) instead of the in-app /my-profile route. This
 * lets a THIRD PARTY — a security guard, building reception, or vendor
 * — scan the badge and confirm the person is a real, currently-active
 * employee WITHOUT needing a PeoplesDock login. Previously the QR
 * pointed at /my-profile, which only worked for the employee scanning
 * their own card while already logged in — not useful to anyone else.
 *
 * Requires an explicit `uuid` prop (the employee's users.uuid, NOT
 * their employee_code) — pass `user?.uuid` from AuthContext (confirmed
 * present in /auth/me's response) rather than relying on whatever
 * shape the `profile` object happens to have, since that comes from a
 * different endpoint (/users/profile) whose exact fields aren't
 * guaranteed to include uuid.
 *
 * The verification page deliberately shows ONLY non-sensitive fields
 * (name, photo, designation, department, company, active/inactive
 * status) — see employee-verify.routes.js and EmployeeVerify.js for
 * the full security design.
 *
 * NEW DEPENDENCIES REQUIRED — run once before deploying:
 *   npm install qrcode.react html2canvas
 *
 * Usage (from ESSHub.js):
 *   <EmployeeIdCard show={showIdCard} onClose={() => setShowIdCard(false)}
 *     profile={p} uuid={user?.uuid} companyName={user?.company_name}
 *     logoUrl={user?.logo_url} primaryColor={user?.primary_color} />
 */
import React, { useRef, useState } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import { Download, X, IdCard, QrCode } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'react-toastify';

const companyInitials = (name = '') =>
    (name || 'CO').split(' ').slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('') || 'CO';

const EmployeeIdCard = ({ show, onClose, profile, uuid, companyName, logoUrl, primaryColor }) => {
    const cardRef = useRef(null);
    const [saving, setSaving] = useState(false);

    const p = profile || {};
    const fullName = [p.first_name, p.last_name].filter(Boolean).join(' ') || 'Employee';
    const initials = [p.first_name?.[0], p.last_name?.[0]].filter(Boolean).join('').toUpperCase() || 'EM';
    const primary  = primaryColor || '#4f46e5';
    const secondary = '#7c3aed'; // matches the gradient style already used in welcome banner

    // FEATURE: QR now encodes the PUBLIC verification page, keyed by the
    // employee's UUID (not their sequential employee_code), so the URL
    // isn't guessable/enumerable. Falls back to /my-profile only if no
    // uuid was passed in, so the QR code is never completely broken.
    const profileUrl = uuid
        ? `${window.location.origin}/verify/${uuid}`
        : `${window.location.origin}/my-profile`;

    const handleSaveToPhone = async () => {
        if (!cardRef.current) return;
        setSaving(true);
        try {
            // Lazy-loaded so html2canvas's bundle size only loads when
            // someone actually clicks Save, not on every ESS Hub visit.
            const html2canvas = (await import('html2canvas')).default;
            const canvas = await html2canvas(cardRef.current, {
                scale: 3, // high-res export for a crisp saved image
                backgroundColor: '#ffffff',
                useCORS: true, // allows company logo / avatar images to render if same-origin-friendly
            });
            const dataUrl = canvas.toDataURL('image/png');
            const a = document.createElement('a');
            a.href = dataUrl;
            a.download = `${fullName.replace(/\s+/g, '_')}_ID_Card.png`;
            a.click();
            toast.success('ID card saved! Check your downloads.');
        } catch (err) {
            toast.error('Could not save the ID card. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal show={show} onHide={onClose} centered>
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="fw-bold fs-6 d-flex align-items-center gap-2">
                    <IdCard size={18} className="text-primary"/>
                    My ID Card
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="d-flex flex-column align-items-center pt-2">
                {/* ── The actual ID card — this exact div is what gets
                     exported to PNG by html2canvas ──────────────────── */}
                <div
                    ref={cardRef}
                    style={{
                        width: 320,
                        borderRadius: 18,
                        overflow: 'hidden',
                        background: '#ffffff',
                        boxShadow: '0 8px 28px rgba(0,0,0,0.12)',
                        border: '1px solid #e5e7eb',
                    }}>
                    {/* Header strip with company branding */}
                    <div style={{
                        background: `linear-gradient(135deg, ${primary}, ${secondary})`,
                        padding: '18px 20px 14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                    }}>
                        {logoUrl ? (
                            <img src={logoUrl} alt={companyName || 'Company'}
                                style={{ height: 28, width: 'auto', maxWidth: 90, objectFit: 'contain' }}/>
                        ) : (
                            <div style={{
                                width: 28, height: 28, borderRadius: 8,
                                background: 'rgba(255,255,255,0.22)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 12, fontWeight: 800, color: '#fff',
                            }}>
                                {companyInitials(companyName)}
                            </div>
                        )}
                        <div style={{ color: '#fff', fontWeight: 700, fontSize: 14, letterSpacing: 0.2 }}>
                            {companyName || 'PeoplesDock'}
                        </div>
                        <div style={{
                            marginLeft: 'auto', color: 'rgba(255,255,255,0.85)',
                            fontSize: 10, fontWeight: 700, letterSpacing: 1,
                            textTransform: 'uppercase', border: '1px solid rgba(255,255,255,0.4)',
                            borderRadius: 20, padding: '3px 10px',
                        }}>
                            ID CARD
                        </div>
                    </div>

                    {/* Photo + name + role */}
                    <div style={{ padding: '22px 20px 16px', textAlign: 'center' }}>
                        {p.avatar ? (
                            <img src={p.avatar} alt={fullName}
                                style={{
                                    width: 84, height: 84, borderRadius: '50%',
                                    objectFit: 'cover', margin: '0 auto 12px',
                                    border: `3px solid ${primary}30`,
                                    display: 'block',
                                }}/>
                        ) : (
                            <div style={{
                                width: 84, height: 84, borderRadius: '50%',
                                background: `${primary}12`, margin: '0 auto 12px',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 28, fontWeight: 800, color: primary,
                                border: `3px solid ${primary}30`,
                            }}>
                                {initials}
                            </div>
                        )}
                        <div style={{ fontSize: 18, fontWeight: 800, color: '#0f172a' }}>
                            {fullName}
                        </div>
                        <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
                            {p.designation_name || p.role_name || 'Employee'}
                        </div>
                        <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 1 }}>
                            {p.department_name || '—'}
                        </div>
                    </div>

                    {/* Divider */}
                    <div style={{ borderTop: '1px dashed #e2e8f0', margin: '0 20px' }}/>

                    {/* Employee code + QR code */}
                    <div style={{
                        padding: '16px 20px 22px',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        gap: 12,
                    }}>
                        <div>
                            <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                Employee Code
                            </div>
                            <div style={{ fontSize: 16, fontWeight: 800, color: primary, marginTop: 2, letterSpacing: 0.5 }}>
                                {p.employee_code || '—'}
                            </div>
                        </div>
                        <div style={{
                            padding: 6, background: '#fff', borderRadius: 8,
                            border: '1px solid #e5e7eb', flexShrink: 0,
                        }}>
                            <QRCodeSVG value={profileUrl} size={68} fgColor="#0f172a" bgColor="#ffffff"/>
                        </div>
                    </div>
                </div>

                <div className="text-muted small mt-3 text-center d-flex align-items-center gap-1">
                    <QrCode size={12}/> Scan the QR code to verify this employee
                </div>
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0">
                <Button variant="light" onClick={onClose}>Close</Button>
                <Button variant="primary" onClick={handleSaveToPhone} disabled={saving}
                    className="d-flex align-items-center gap-2">
                    {saving ? <Spinner size="sm"/> : <Download size={14}/>}
                    {saving ? 'Saving…' : 'Save to Phone'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default EmployeeIdCard;
